<template>
  <div>
    <h4>{{ editingId ? "Modifier la combinaison" : "Créer une combinaison" }}</h4>

    <b-form @submit.prevent="saveCombination">
      <b-form-group label="Nom de la combinaison">
        <b-form-input v-model="name" required placeholder="Nom de la combinaison" />
      </b-form-group>

    <b-form-group label="Sélectionnez les combinaisons">
  <div class="d-flex flex-wrap gap-2">
    <b-button
      v-for="option in commentOptions"
      :key="option.value"
      :variant="selectedComments.includes(option.value) ? 'primary' : 'outline-primary'"
      size="sm"
      @click="toggleComment(option.value)"
    >
      {{ option.text }}
    </b-button>
  </div>
</b-form-group>


      <b-form-group label="Texte associé">
        <b-form-textarea v-model="text" rows="3" placeholder="Texte associé..." />
      </b-form-group>

      <b-form-group label="Image">
        <b-form-file 
          v-model="image" 
          accept="image/*" 
          @change="onImageChange"
        />
        <div v-if="imagePreview" class="mt-2">
          <p>Aperçu :</p>
          <img :src="imagePreview" alt="Aperçu image" style="max-height:150px;" />
        </div>
      </b-form-group>

      <b-button type="submit" variant="primary" :disabled="!canSubmit">
        {{ editingId ? "Mettre à jour" : "Enregistrer" }}
      </b-button>
      <b-button v-if="editingId" variant="secondary" @click="cancelEdit" class="ml-2">
        Annuler
      </b-button>
    </b-form>

    <hr>

    <h5 class="mt-3">Combinaisons existantes</h5>
    <b-table :items="combinations" :fields="fields" responsive="sm">
      <template #cell(comments)="data">
        {{ formatComments(data.item.comments) }}
      </template>
      <template #cell(actions)="data">
        <b-button size="sm" variant="warning" @click="editCombination(data.item)">Modifier</b-button>
        <b-button size="sm" variant="danger" class="ml-1" @click="deleteCombination(data.item.id)">Supprimer</b-button>
      </template>
      <template #cell(imageUrl)="data">
        <img v-if="data.item.imageUrl" :src="data.item.imageUrl" alt="image" style="height:50px;" />
        <span v-else>—</span>
      </template>
    </b-table>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "TestCombinaisons",
  props: { testId: { type: String, required: true } },

  data() {
    return {
      name: "",
      text: "",
      image: null,
      imagePreview: null, 
      selectedComments: [],
      commentOptions: [],
      combinations: [],
      editingId: null,

      fields: [
        { key: "name", label: "Nom" },
        { key: "comments", label: "Items intermédiaires" },
        { key: "text", label: "Texte" },
        { key: "imageUrl", label: "Image" },
        { key: "actions", label: "Actions" }
      ]
    };
  },

  computed: {
    canSubmit() {
      return this.name.trim() !== "";
    }
  },

  methods: {
    toggleComment(val) {
    const index = this.selectedComments.indexOf(val);
    if (index === -1) {
      this.selectedComments.push(val);
    } else {
      this.selectedComments.splice(index, 1);
    }
  },

  async loadComments() {
    try {
      const res = await axios.get(`/api/tests/${this.testId}/letter-combi`);
      this.commentOptions = res.data.map(c => ({ value: c, text: c }));
    } catch (err) {
      console.error("Erreur lors du chargement des combinaisons :", err);
      this.commentOptions = [];
    }
  },

    async loadCombinations() {
      const res = await axios.get(`/api/tests/${this.testId}/combi`);
      this.combinations = res.data;
    },

    formatComments(commentIds) {
      return commentIds.map(cid => {
        const c = this.commentOptions.find(o => o.value === cid);
        return c ? c.text : cid;
      }).join(", ");
    },

    onImageChange(event) {
      if (event.target.files && event.target.files[0]) {
        this.imagePreview = URL.createObjectURL(event.target.files[0]);
      }
    },

    async saveCombination() {
      try {
        const formData = new FormData();
        formData.append("name", this.name);
        formData.append("text", this.text);
        formData.append("comments", JSON.stringify(this.selectedComments));
        if (this.image) formData.append("image", this.image);

        if (this.editingId) {
          await axios.put(`/api/combi/${this.editingId}`, formData);
          this.$bvToast.toast("Combinaison mise à jour avec succès", { title: "Succès", variant: "success", solid: true });
          this.editingId = null;
        } else {
          await axios.post(`/api/tests/${this.testId}/combi`, formData);
          this.$bvToast.toast("Combinaison créée avec succès", { title: "Succès", variant: "success", solid: true });
        }

        this.resetForm();
        this.loadCombinations();
      } catch (err) {
        this.$bvToast.toast(err.response?.data?.error || "Erreur lors de l’enregistrement", { title: "Erreur", variant: "danger", solid: true });
      }
    },

    editCombination(item) {
      this.name = item.name;
      this.text = item.text;
      this.selectedComments = item.comments;
      this.editingId = item.id;
      this.imagePreview = item.imageUrl || null;
      this.image = null;
    },

    cancelEdit() {
      this.resetForm();
      this.editingId = null;
    },

    async deleteCombination(combId) {
      if (!confirm("Voulez-vous vraiment supprimer cette combinaison ?")) return;
      try {
        await axios.delete(`/api/combi/${combId}`);
        this.$bvToast.toast("Combinaison supprimée", { title: "Succès", variant: "success", solid: true });
        this.loadCombinations();
      } catch (err) {
        this.$bvToast.toast(err.response?.data?.error || "Erreur lors de la suppression", { title: "Erreur", variant: "danger", solid: true });
      }
    },

    resetForm() {
      this.name = "";
      this.text = "";
      this.image = null;
      this.imagePreview = null;
      this.selectedComments = [];
    }
  },

  mounted() {
    this.loadComments();
    this.loadCombinations();
  }
};
</script>
